import { useQuery } from '@tanstack/react-query';

import { apiClient } from './api-client';

/**
 * Reporting (spec §59).
 *
 * Every figure the dashboard shows comes from here. Nothing on this side
 * computes a total, a share or a percentage change — the server owns those, so
 * the dashboard and the screen a number also appears on cannot disagree.
 */

export const REPORT_PERIODS = [7, 30, 90, 365] as const;
export type ReportPeriod = (typeof REPORT_PERIODS)[number];

export const PERIOD_LABEL: Record<ReportPeriod, string> = {
  7: 'Last 7 days',
  30: 'Last 30 days',
  90: 'Last 90 days',
  365: 'Last year',
};

export interface DashboardOverview {
  revenue: number;
  /** Null when the previous period was zero — growth from nothing is undefined. */
  revenueChangePercent: number | null;
  orders: number;
  ordersChangePercent: number | null;
  averageOrderValue: number;
  newCustomers: number;
  newCustomersChangePercent: number | null;
  periodDays: number;
}

export interface TrendPoint {
  date: string;
  revenue: number;
  orders: number;
}

export interface ShareSlice {
  name: string;
  value: number;
  percent: number;
}

export interface TopProduct {
  name: string;
  slug: string | null;
  sold: number;
  revenue: number;
}

export interface TopCustomer {
  id: string;
  customerCode: string | null;
  name: string;
  orders: number;
  spent: number;
}

const keys = {
  all: ['admin', 'reports'] as const,
  overview: (days: number) => [...keys.all, 'overview', days] as const,
  trend: (days: number) => [...keys.all, 'trend', days] as const,
  orderStatus: () => [...keys.all, 'order-status'] as const,
  categories: (days: number) => [...keys.all, 'categories', days] as const,
  topProducts: (days: number) => [...keys.all, 'top-products', days] as const,
  topCustomers: (days: number) => [...keys.all, 'top-customers', days] as const,
};

export function useOverview(days: ReportPeriod) {
  return useQuery({
    queryKey: keys.overview(days),
    queryFn: () =>
      apiClient.get<DashboardOverview>('/admin/reports/overview', { query: { days } }),
    placeholderData: (previous) => previous,
  });
}

export function useSalesTrend(days: ReportPeriod) {
  return useQuery({
    queryKey: keys.trend(days),
    queryFn: () => apiClient.get<TrendPoint[]>('/admin/reports/sales-trend', { query: { days } }),
    placeholderData: (previous) => previous,
  });
}

export function useOrderStatusBreakdown() {
  return useQuery({
    queryKey: keys.orderStatus(),
    queryFn: () => apiClient.get<ShareSlice[]>('/admin/reports/order-status'),
  });
}

export function useCategorySales(days: ReportPeriod) {
  return useQuery({
    queryKey: keys.categories(days),
    queryFn: () =>
      apiClient.get<ShareSlice[]>('/admin/reports/category-sales', { query: { days } }),
    placeholderData: (previous) => previous,
  });
}

export function useTopProducts(days: ReportPeriod) {
  return useQuery({
    queryKey: keys.topProducts(days),
    queryFn: () => apiClient.get<TopProduct[]>('/admin/reports/top-products', { query: { days } }),
    placeholderData: (previous) => previous,
  });
}

export function useTopCustomers(days: ReportPeriod) {
  return useQuery({
    queryKey: keys.topCustomers(days),
    queryFn: () =>
      apiClient.get<TopCustomer[]>('/admin/reports/top-customers', { query: { days } }),
    placeholderData: (previous) => previous,
  });
}

/** Chart palette — accessible against both the card and canvas backgrounds. */
export const CHART_COLORS = ['#2563eb', '#10b981', '#f97316', '#8b5cf6', '#ef4444', '#64748b'];
